import { PREFISSI_OPTIONS } from '@/src/lib/constants/options'
import type { FlowConfig } from '@/src/lib/flow-engine/types'

export const bookingWorkerFlow: FlowConfig = {
  id: 'booking-worker',
  path: '/booking-worker',
  title: 'flows.booking-worker.title',
  steps: [
    // ── Step 1.1 — Entry / Registration ─────────────────────
    {
      id: 1,
      slug: 'registration',
      group: 1,
      title: 'steps.registration.title',
      shortTitle: 'steps.registration.shortTitle',
      summaryTitle: 'summary.workerGroups.1',
      layout: 'centered',
      fields: [
        {
          key: 'nome',
          label: 'fields.nome_worker.label',
          type: 'text',
          required: true,
          placeholder: 'fields.nome_worker.placeholder',
          validation: { minLength: 2 }
        },
        {
          key: 'cognome',
          label: 'fields.cognome_worker.label',
          type: 'text',
          required: true,
          placeholder: 'fields.cognome_worker.placeholder',
          validation: { minLength: 2 }
        },
        {
          key: 'privacy',
          label: 'fields.privacy.label',
          checkboxText: 'fields.privacy.checkboxText',
          type: 'checkbox',
          required: true,
          validation: { mustBeTrue: true }
        },
        {
          key: 'autorizzazione_servizi',
          label: 'fields.autorizzazione_servizi.label',
          checkboxText: 'fields.autorizzazione_servizi.checkboxText',
          type: 'checkbox',
          required: false,
          validation: { mustBeTrue: false }
        },
        {
          key: 'occupazione',
          label: 'fields.occupazione.label',
          type: 'hidden',
          defaultValue: 1
        }
      ]
    },

    // ── Step 1.2 — City + Contact info ──────────────────────
    {
      id: 2,
      slug: 'contacts',
      group: 1,
      title: 'steps.contacts.title',
      titleTemplate: 'steps.contacts.titleTemplate',
      shortTitle: 'steps.contacts.shortTitle',
      description: 'steps.contacts.description',
      layout: 'centered',
      fields: [
        {
          key: 'email',
          label: 'fields.email_worker.label',
          type: 'email',
          required: true,
          placeholder: 'fields.email_worker.placeholder'
        },
        {
          key: 'prefisso',
          label: 'fields.prefisso.label',
          type: 'select',
          required: true,
          sortOptions: false,
          options: PREFISSI_OPTIONS
        },
        {
          key: 'cellulare',
          label: 'fields.cellulare_worker.label',
          type: 'tel',
          required: true,
          validation: { minLength: 6, maxLength: 15 }
        }
      ]
    },

    // ── Step 1.3 — City selection ────────────────────────────
    {
      id: 3,
      saveStep: 1,
      saveOnNext: true,
      slug: 'city',
      group: 1,
      title: 'steps.city.title',
      shortTitle: 'steps.city.shortTitle',
      description: 'steps.city.description',
      layout: 'form',
      fullWidth: true,
      fields: [
        {
          key: 'citta_1',
          label: 'fields.citta_1.label',
          type: 'city-grid',
          required: true,
          remoteOptions: {
            endpoint: '/api/booking-worker/lookups/cities/',
            valueKey: 'slug',
            labelKey: 'name',
            imageBasePath: '/images'
          }
        }
      ]
    },

    // ── Step 2 — Dati personali ──────────────────────────────
    {
      id: 4,
      saveStep: 2,
      saveOnNext: true,
      slug: 'personal-data',
      group: 2,
      title: 'steps.personal-data.title',
      shortTitle: 'steps.personal-data.shortTitle',
      summaryTitle: 'summary.workerGroups.2',
      description: 'steps.personal-data.description',
      layout: 'form',
      showSideSummary: true,
      fields: [
        {
          key: 'nazione_residenza',
          label: 'fields.nazione_residenza.label',
          type: 'select',
          required: true,
          colSpan: 6,
          remoteOptions: {
            endpoint: '/api/lookups/countries/',
            valueKey: 'value',
            labelKey: 'label'
          }
        },
        {
          key: 'sesso',
          label: 'fields.sesso.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          options: [
            { value: 'M', label: 'options.sesso.M' },
            { value: 'F', label: 'options.sesso.F' }
          ]
        },
        {
          key: 'data_nascita',
          label: 'fields.data_nascita.label',
          type: 'date-picker-airbnb',
          required: true,
          colSpan: 6,
          validation: { minAge: 16 }
        }
      ]
    },

    // ── Step 3 — Alloggio ──────────────────────────────────
    {
      id: 5,
      saveStep: 3,
      saveOnNext: true,
      slug: 'accommodation',
      group: 3,
      title: 'steps.accommodation.title',
      shortTitle: 'steps.accommodation.shortTitle',
      description: 'steps.accommodation.description',
      layout: 'form',
      showSideSummary: true,
      fields: [
        {
          key: 'sede_camplus_1',
          label: 'fields.sede_camplus_1.label',
          type: 'select',
          required: true,
          colSpan: 6,
          remoteOptions: {
            endpoint: '/api/booking-worker/lookups/structures/',
            valueKey: 'slug',
            labelKey: 'name',
            extraFields: {
              tipologia_1: 'tipologia'
            },
            dependsOn: 'citta_1'
          }
        },
        {
          key: 'bando_1',
          label: 'fields.bando_1.label',
          type: 'hidden',
          defaultValue: false
        },
        {
          key: 'tipologia_1',
          label: 'fields.tipologia_1.label',
          type: 'hidden',
          defaultValue: false
        },
        {
          key: 'sistemazione_1',
          label: 'fields.sistemazione_1.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          remoteOptions: {
            endpoint: '/api/booking-worker/lookups/rooms/',
            valueKey: 'slug',
            labelKey: 'name',
            dependsOn: 'sede_camplus_1'
          }
        },
        {
          key: 'checkin',
          label: 'fields.checkin.label',
          type: 'date-picker-airbnb',
          required: true,
          colSpan: 6,
          validation: { disabledMonths: [6, 7] }
        },
        {
          key: 'durata',
          label: 'fields.durata.label',
          type: 'select',
          required: true,
          colSpan: 6,
          sortOptions: false,
          options: [
            { value: '6', label: 'options.durata.6' },
            { value: '12', label: 'options.durata.12' }
          ]
        }
      ]
    },

    // ── Step 4 — Additional info + Declarations ────────────
    {
      id: 6,
      slug: 'completion',
      group: 4,
      title: 'steps.completion.title',
      shortTitle: 'steps.completion.shortTitle',
      summaryTitle: 'summary.workerGroups.3',
      description: 'steps.completion.description',
      layout: 'form',
      showSideSummary: true,
      fields: [
        {
          key: 'conoscenza_camplus',
          label: 'fields.conoscenza_camplus.label',
          type: 'select',
          required: true,
          sortOptions: false,
          options: [
            { value: 'eventi', label: 'options.conoscenza_camplus.eventi' },
            { value: 'parenti-amici', label: 'options.conoscenza_camplus.parenti-amici' },
            { value: 'social-media', label: 'options.conoscenza_camplus.social-media' },
            {
              value: 'sito-universita',
              label: 'options.conoscenza_camplus.sito-universita'
            },
            {
              value: 'ricerca-online',
              label: 'options.conoscenza_camplus.ricerca-online'
            },
            {
              value: 'campagna-pubblicitaria',
              label: 'options.conoscenza_camplus.campagna-pubblicitaria'
            },
            { value: 'altro', label: 'options.conoscenza_camplus.altro' }
          ]
        },
        {
          key: 'specifica_conoscenza',
          label: 'fields.specifica_conoscenza.label',
          type: 'text',
          required: true,
          placeholder: 'fields.specifica_conoscenza.placeholder',
          visibleWhen: {
            field: 'conoscenza_camplus',
            operator: 'eq',
            value: 'altro'
          }
        },
        {
          key: 'testo_accoglienza',
          label: 'fields.testo_accoglienza.label',
          type: 'textarea',
          required: false,
          placeholder: 'fields.testo_accoglienza.placeholder',
          validation: { maxLength: 500 }
        },
        {
          key: 'condanne_penali',
          label: 'fields.condanne_penali.label',
          type: 'checkbox',
          required: true,
          validation: { mustBeTrue: true }
        },
        {
          key: 'patologie',
          label: 'fields.patologie.label',
          type: 'checkbox',
          required: true,
          validation: { mustBeTrue: true }
        }
      ]
    },

    // ── Step 5 — Summary ────────────────────────────────────
    {
      id: 7,
      saveStep: 4,
      saveOnNext: true,
      slug: 'summary',
      group: 5,
      title: 'steps.summary.title',
      shortTitle: 'steps.summary.shortTitle',
      description: 'steps.summary.description',
      layout: 'summary',
      fields: [],
      isFinal: true
    }
  ],
  successRedirectRules: [
    {
      name: 'Waitlist Workers',
      redirectUrl: {
        it: 'https://www.camplus.it/feedback-domanda-di-ammissione-lavoratori-lista-attesa/',
        en: 'https://www.camplus.it/en/feedback-waiting-lists-application-for-worker-admission/',
        fr: 'https://www.camplus.it/fr/listes-dattente-pour-les-demandes-dadmission-des-travailleurs/',
        es: 'https://www.camplus.it/es/listas-de-espera-para-retroalimentacion-solicitud-de-admision-de-trabajadores/'
      },
      conditions: [{ field: 'lista_attesa', operator: 'eq', value: true }]
    }
  ],
  fallbackUrl: {
    it: 'https://www.camplus.it/feedback-domanda-di-ammissione-lavoratori/',
    en: 'https://www.camplus.it/en/call-for-application-feedback-professionals/',
    fr: 'https://www.camplus.it/fr/formulaire-de-retour-demande-dadmission-travailleurs/',
    es: 'https://www.camplus.it/es/feedback-solicitud-de-admision-trabajadores/'
  }
}

/** Helpers */
export function getWorkerStepBySlug(slug: string) {
  return bookingWorkerFlow.steps.find((s) => s.slug === slug)
}

export function getWorkerStepById(id: number) {
  return bookingWorkerFlow.steps.find((s) => s.id === id)
}

export function getWorkerStepBySaveStep(saveStep: number) {
  return [...bookingWorkerFlow.steps].reverse().find((s) => (s.saveStep ?? s.id) === saveStep)
}
